import type { ScheduleUpdateNotification } from "../../../../types/schedule-updates";

/**
 * Pure helpers for describing a schedule-update notification. Kept in their own
 * module (not the banner component file) so both the banner and the updates list
 * can import them without tripping react-refresh/only-export-components.
 */

/**
 * Pluralize a single count clause, e.g. (4, "new game") -> "4 new games".
 * Omits the "s" for a count of 1. Callers filter out zero counts before this.
 */
function clause(count: number, singular: string): string {
  return `${count} ${singular}${count === 1 ? "" : "s"}`;
}

/**
 * Build the "what changed" sentence from a notification's counts. Omits any
 * zero-count clause and pluralizes each surviving clause. Returns "" when
 * every count is zero, which callers treat as "nothing to show".
 *
 * Copy is plain and human, no em dashes:
 *   "Schedule updated: 4 new games, 2 changed, 1 needs review."
 */
export function describeScheduleUpdate(
  n: Pick<
    ScheduleUpdateNotification,
    "addedCount" | "updatedCount" | "removedCount" | "conflictCount"
  >
): string {
  const clauses: string[] = [];
  if (n.addedCount > 0) clauses.push(clause(n.addedCount, "new game"));
  if (n.updatedCount > 0) clauses.push(`${n.updatedCount} changed`);
  if (n.removedCount > 0) clauses.push(clause(n.removedCount, "removed game"));
  if (n.conflictCount > 0) {
    clauses.push(n.conflictCount === 1 ? "1 needs review" : `${n.conflictCount} need review`);
  }
  if (clauses.length === 0) return "";
  return `Schedule updated: ${clauses.join(", ")}.`;
}

/** True when the notification carries at least one non-zero change count. */
export function hasChanges(n: ScheduleUpdateNotification): boolean {
  return (
    n.addedCount > 0 ||
    n.updatedCount > 0 ||
    n.removedCount > 0 ||
    n.conflictCount > 0
  );
}
